import React from "react";
import { Rocket, Check, ShieldCheck, Sparkles, Clock, Target, Puzzle, Search } from "lucide-react";
import FeaturePage, { MockWindow, ScoreBar } from "./FeaturePage";
import { siblingLinks } from "./links";
import VideoEmbed from "../../components/VideoEmbed";
import { AUTOPILOT_VIDEO_ID } from "../../lib/video";

const ACCENT = "#EA580C";

// Every number and claim on this page is checked against what the product actually does:
//   · "tailored to each job"      tailor_resume_for_apply(resume_text, jd_text, …) in server.py
//   · "scored before and after"   score_resume_pair(before, after, …) — one judge, both résumés,
//                                 so the DIFFERENCE is meaningful rather than two noisy numbers
//   · "missing skills"            skill_gaps / missing_skills, each {skill, why}
//   · "about a minute"            measured 40.6s tailoring + ~10s for the rest, run concurrently
//   · "nothing is sent…"          review_before_submit defaults ON; the engine fills to Review
//                                 and stops — the submit is a separate, explicit action
// If any of those stop being true, this page is a false statement, not stale marketing.

// ── Hero: the video is the pitch. Nothing describes this feature as well as watching it run. ──
function HeroVisual() {
  return (
    <div className="space-y-3">
      <VideoEmbed id={AUTOPILOT_VIDEO_ID} title="How VetraHub Autopilot works" />
      <div className="flex items-center gap-1.5 font-mono text-[10px] text-muted-foreground">
        <Clock className="w-3 h-3" style={{ color: ACCENT }} /> See a real application go out, start to finish
      </div>
    </div>
  );
}

// ── Row visual: the before/after score, which is the single most persuasive artefact we have ──
function ScoreVisual() {
  return (
    <MockWindow label="RESUME, TAILORED TO THIS JOB" badge="+16" accent={ACCENT}>
      <div className="space-y-4">
        <ScoreBar label="YOUR RESUME, AS WRITTEN" value={62} accent="#94A3B8" suffix="/100" />
        <ScoreBar label="TAILORED FOR THIS ROLE" value={78} accent={ACCENT} suffix="/100" />
        <div className="rounded-xl border border-border p-3.5">
          <div className="font-mono text-[10px] tracking-[0.15em] mb-2.5" style={{ color: ACCENT }}>SKILLS THIS JOB WANTS</div>
          <div className="flex flex-wrap gap-1.5">
            {[["CI/CD", true], ["Playwright", true], ["API testing", true], ["Kubernetes", false]].map(([k, on]) => (
              <span key={k} className={`inline-flex items-center gap-1 font-mono text-[10px] px-2 py-1 rounded border ${
                on ? "border-[#10B981]/30 bg-[#10B981]/10 text-[#047857]" : "border-[#F59E0B]/30 bg-[#F59E0B]/10 text-[#B45309]"
              }`}>
                {on ? <Check className="w-2.5 h-2.5" /> : <Sparkles className="w-2.5 h-2.5" />} {k}
              </span>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-1.5 font-mono text-[10px] text-muted-foreground">
          <Target className="w-3 h-3" style={{ color: ACCENT }} /> 1 gap worth closing before you apply
        </div>
      </div>
    </MockWindow>
  );
}

// ── Row visual: review-before-submit. The objection this feature has to answer. ──
function ReviewVisual() {
  return (
    <MockWindow label="READY FOR YOUR REVIEW" badge="3 waiting" accent={ACCENT}>
      <div className="space-y-2.5">
        {[["Senior QA Automation Lead", "Shopify", "95%"],
          ["SDET Manager", "Epic Games", "91%"],
          ["QA Lead, Test Automation", "Citi", "88%"]].map(([role, co, score]) => (
          <div key={role} className="flex items-center gap-3 rounded-xl border border-border bg-white p-3">
            <span className="w-7 h-7 rounded-lg grid place-items-center flex-shrink-0"
                  style={{ background: `${ACCENT}1a`, color: ACCENT }}>
              <Check className="w-3.5 h-3.5" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[12px] font-semibold text-[#0F172A] truncate">{role}</div>
              <div className="text-[10px] text-muted-foreground truncate">{co}</div>
            </div>
            <span className="font-mono text-[10px] font-bold flex-shrink-0" style={{ color: ACCENT }}>{score}</span>
          </div>
        ))}
        <div className="flex items-center gap-1.5 font-mono text-[10px] text-muted-foreground pt-1">
          <ShieldCheck className="w-3 h-3" style={{ color: ACCENT }} /> Nothing is sent until you say so
        </div>
      </div>
    </MockWindow>
  );
}

export default function FeatureAutopilot() {
  return (
    <FeaturePage
      accent={ACCENT}
      eyebrow="Autopilot"
      title="Stop rewriting your resume"
      titleAccent="for every job."
      subtitle="Autopilot finds roles that fit, tailors your resume to each one, fills in the employer's form, and waits for your approval before anything is sent."
      heroChips={[
        "Resume tailored per job — automatically",
        "You approve every application",
        "About a minute per application",
      ]}
      heroVisual={<HeroVisual />}
      rows={[
        {
          tag: "NO MORE MANUAL TAILORING",
          title: "Your resume, rewritten for the job you're actually applying to",
          body: "Everyone knows you should tailor your resume to each posting. Almost nobody does it, because it takes twenty minutes a job. Autopilot reads the description and rewrites your resume against it — the same work, done for you, for every single application.",
          bullets: [
            "Scores your resume before and after, so you can see what changed and by how much",
            "Names the skills the job asks for that your resume doesn't show yet",
            "Uses your own experience — it rewrites how you say it, never invents what you did",
          ],
          visual: <ScoreVisual />,
        },
        {
          tag: "YOU STAY IN CONTROL",
          title: "Nothing is submitted until you've read it",
          body: "Autopilot prepares each application completely — resume, cover letter, screening questions — and then stops. You see exactly what will be sent, to whom, and can change or discard it. Approving is one click; so is throwing it away.",
          bullets: [
            "Review every application before it goes out, as the default",
            "See the exact answers given to each employer's questions",
            "Turn it off for any job, or any employer, at any time",
          ],
          visual: <ReviewVisual />,
          reverse: true,
        },
      ]}
      steps={[
        { title: "Tell us what you're looking for", body: "Job titles and locations, once. Autopilot keeps looking in the background and ranks what it finds against your resume." },
        { title: "We prepare the application", body: "Your resume is tailored to that specific job, the form is filled from your saved details, and screening questions are answered from your own words." },
        { title: "You review and approve", body: "Everything waits for you. Read it, change anything, then approve — or discard it and nothing happens." },
        { title: "It's submitted for you", body: "Autopilot sends it through the employer's own system and tracks it, including any reply that comes back." },
      ]}
      useCases={[
        { icon: Clock, persona: "Anyone applying at volume", body: "Twenty tailored applications is a week of evenings by hand. This is the same work without the evenings." },
        { icon: Search, persona: "People who are still employed", body: "The search runs while you're at work. You just approve what's worth applying to." },
        { icon: Target, persona: "Career switchers", body: "See the skills each target role expects that your resume doesn't show yet — before you apply, not after the rejection." },
      ]}
      faqs={[
        {
          q: "Will it apply to jobs without asking me?",
          a: "No. Autopilot prepares each application and waits for your approval — that's the default and we'd rather you keep it. You see the tailored resume and every answer before anything is sent, and discarding an application takes one click.",
        },
        {
          q: "Does it make things up on my resume?",
          a: "No. It rewrites how your existing experience is described so it matches the language the job uses — it never adds work you didn't do. You can always send your resume exactly as you wrote it instead.",
        },
        {
          q: "Why do some employers need the browser extension?",
          a: "A few application systems only accept an application sent from your own browser. Our Chrome extension handles those: your application is prepared as usual and waits until the extension can submit it. It's free, it only runs on the job sites it lists, and installing it takes a few seconds. Everything else is submitted without it.",
        },
        {
          q: "How long does one application take?",
          a: "About a minute from approval to submitted — most of which is tailoring your resume to the job. The search runs in the background before that, so there's nothing to wait for when you sit down.",
        },
        {
          q: "What does it do about screening questions?",
          a: "It answers them from your profile and your previous answers, and saves them so the next employer asking the same thing gets the same answer. Anything it isn't confident about is handed back to you rather than guessed at.",
        },
        {
          q: "Can I see what was actually sent?",
          a: "Yes — every application keeps a full record of the resume, the cover letter, and each question and answer exactly as the employer received them.",
        },
      ]}
      ctaTitle="Let it do the applying."
      ctaSubtitle="Free to start. You approve everything before it's sent."
      related={siblingLinks("autopilot")}
      seoTitle="Autopilot — Auto-Apply With a Resume Tailored to Every Job | VetraHub"
      seoDescription="Autopilot finds matching jobs, tailors your resume to each one, fills in the employer's application and waits for your approval before anything is sent. About a minute per application. Free to start."
    />
  );
}
